// DOCS-KB: a doc link pasted in chat unfurls as a compact card (kind icon, title, space, "edited
// 2h ago"; the whole card a link), from `pages.get` and the cached spaces list. `docLinksIn` finds
// the page ids of `/c/{slug}/work/p/{id}` links in a message's text (this company's only, once
// each, in order). Chat renders the cards beside its task and approval embeds.
import { cn } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { ago, docIcon } from "./doc-kinds";
import { kindLabel } from "./page-dialogs";
import { pendingAsNull, spacesQuery, workKeys } from "./work-queries";

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The page ids of this company's doc links in `text`, deduplicated, in order of appearance. */
export function docLinksIn(text: string, slug: string): string[] {
  const re = new RegExp(
    `/c/${escapeRe(slug)}/work/p/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?![0-9a-f-])`,
    "gi",
  );
  return [...new Set(Array.from(text.matchAll(re), (m) => (m[1] ?? "").toLowerCase()))];
}

export function DocLinkCard({
  api,
  company,
  pageId,
}: {
  api: Api;
  company: CompanyAccess;
  pageId: string;
}) {
  // Its own key: a 404 (not visible, or deleted) reads as no card, without the error toast.
  const page = useQuery({
    queryKey: [...workKeys.page(company.id, pageId), "card"],
    queryFn: () =>
      pendingAsNull(() => api.client.pages.get({ params: { co: company.id, id: pageId } })),
    retry: false,
  });
  const spaces = useQuery(spacesQuery(api, company.id));
  // A doc the reader may not see (or one deleted) stays a plain link in the text.
  if (!page.data || page.data.kind === "task_body") return null;
  const p = page.data;
  const Icon = docIcon(p.kind);
  const space = spaces.data?.data.find((s) => s.id === p.space_id)?.name;
  return (
    <Link
      to="/c/$slug/work/p/$pageId"
      params={{ slug: company.slug, pageId: p.id }}
      search={keepFocus}
      data-slot="doc-link-card"
      aria-label={`${kindLabel(p.kind)}: ${p.title}`}
      className={cn(
        "flex max-w-md items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 hover:bg-sunken",
        "outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring focus-visible:outline-solid",
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0 text-faint" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{p.title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {[space, kindLabel(p.kind), `edited ${ago(p.updated_at)}`].filter(Boolean).join(" · ")}
        </span>
      </span>
    </Link>
  );
}
